import { useCallback, useEffect, useRef, useState } from "react";
import type {
  CompletionNoteSectionKey,
  MedicalEvidenceTaskKey,
  FormSubmission,
  PreConsultSummary,
  TransferReview,
  TransferReviewDecision,
  TransferReviewUpload,
  ClinicalPhase,
  PlannedMedicationView,
  TreatmentStartPrecision,
  UpdateJournalSections,
  MeasurementReviewStatus,
} from "@care-by-ella/contracts";
import {
  canTransitionTransferReview,
  finalizeTreatmentComment,
  treatmentCommentPrefill,
  measurementSourceLabel,
  COMPLETION_STANDARD_PATIENT_TEXT,
  MEDICAL_EVIDENCE_TASK_KEYS,
  OPEN_TRANSFER_REVIEW_STATUSES,
  PUBLISH_DECISION_AND_SEND_LINK_CTA,
  PUBLISH_TO_PATIENT_CTA,
  transferReviewDecisionSchema,
  assembleCompletionNoteText,
  assembleJournalTextFor,
  COMPLETION_NOTE_LAYOUT,
  emptyJournalHeadingsFor,
  transferJournalDecision,
  TRANSFER_JOURNAL_LAYOUT,
  transferDecisionRound,
  transferJournalOrder,
  prescriptionLabel,
  PLANNED_MEDICATION_FIELD_LABEL,
  TRANSFER_RECEPT_PRESCRIPTION_NEED_LABEL,
  TRANSFER_RECEPT_SAVED_WITH_COMPLETION,
} from "@care-by-ella/contracts";
import {
  decideTransferReview,
  fetchPatientUploadBlob,
  fetchTransferReviewJournal,
  saveTransferReviewJournal,
  fetchSubscriptionPlans,
  fetchMedicalEvidence,
  fetchCompletionNote,
  UnauthorizedError,
  ValidationError,
} from "@/lib/clinician-api";
import { patientAttachmentsHeading } from "@/lib/evidence-uploads";
import {
  errorSummary,
  fieldDomId,
  findFieldElement,
  focusFirstInvalid,
  mapServerIssues,
  orderedFieldErrors,
  type FieldSpec,
  type ServerIssue,
} from "@/lib/form-errors";
import {
  completionGroupLabel,
  remainingSupplyLabel,
  transferReviewStatusLabel,
} from "@/lib/state-labels";
import { RiskFlagList, SummarySections } from "./SummarySections";
import { answerAnchorId, linkedAnswerKey } from "@/lib/flag-links";
import { orderFieldErrors, toOrder } from "./PrescriptionOrderFields";
import {
  RECEPT_SECTION_ID,
  TransferReceptSection,
} from "./TransferReceptSection";
import {
  initialRecept,
  receptApprovalParts,
  receptChoiceProblems,
  receptPlanChoiceLabels,
  receptPlannedMedication,
  receptPrescriptionPreset,
  receptReturning,
  type TransferReceptValue,
} from "@/lib/transfer-recept";
import { fetchPlannedMedication } from "@/lib/treatment-start-api";
import { completionNoteRoundId } from "@/lib/completion-round";
import { CompletionNoteWorkspace } from "./CompletionNoteWorkspace";
import { ClinicalPhaseField } from "./ClinicalPhaseField";
import { EvidenceSummaryCard } from "./EvidenceSummaryCard";
import { MeasurementReviewTag } from "./MeasurementReview";
import { GAP_RISK_BADGE } from "@/lib/gap-risk-badge";
import {
  AiSummarisedHeadings,
  JournalSectionsCard,
  type JournalSectionsHandle,
  type JournalValues,
} from "./JournalSectionsCard";
import { autoSummaryProgressLabel } from "@/lib/journal-auto-summary";
import {
  confirmedNoteStillCurrent,
  noteInputChanged,
  renewalWebdocBlocker,
} from "@/lib/renewal-webdoc";
import { transferWebdocControl } from "@/lib/transfer-webdoc";
import { useTransferJournalAi } from "@/lib/use-transfer-journal-ai";
import { sentPlanLine, type PlanAmounts } from "@/lib/transfer-plan";
import { TransferPlanChoiceCard } from "./TransferPlanChoiceCard";
import { PrescriptionEvidenceReviewControl } from "./PrescriptionEvidenceReviewControl";
import { TreatmentCommentField } from "./TreatmentCommentField";
import { doctorNoticeText } from "@/lib/doctor-notice";
import {
  DECISION_CHOICES,
  EMPTY_PRESCRIPTION_CHOICE,
  prescriptionDecisionPayload,
  WebdocControlStep,
  type DecisionPrescriptionChoice,
  type PrescriptionChoiceValue,
} from "./WebdocControlStep";
import {
  CompletionPicker,
  CompletionProgress,
  completionPayload,
  completionProblem,
  type CompletionValue,
} from "./CompletionPicker";
import { useReadOnly } from "./SupportView";

// TES-950 — the komplettering list opens with its outcome already decided by
// the button the doctor pressed.
const COMPLETION_START: CompletionValue = {
  outcome: "completion",
  items: [],
  otherText: "",
  otherHeading: "",
  otherUploadAllowed: false,
};

// TES-457/458 — the "receptövertag" case view. The review's status and who
// decided it, then the two underlag side by side the way the doctor reads
// them: F-07 (what she is on, her attachments under the section they were
// uploaded in, the blood-pressure/pulse reading the submit recorded) and
// F-02 (the health declaration the review was OPENED on — not her latest).
// Both summaries come from the API rendered against the template version
// each was submitted under, so a wording change after submit can never
// desync this page from what she answered.
//
// While the review is open, her decision: "godkänn" / "kan inte tas över" /
// "vårdmöte behövs" / "begär komplettering". Which buttons show follows the
// shared transition table, so the panel can never offer what the API 409s.

function fmt(date: string) {
  return new Date(date).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

type Outcome = TransferReviewDecision["outcome"];

// TES-1262 — the Kompletteringsanteckning the decision is compiled into.
type CompletionNoteTarget = {
  noteDate: string;
  sections: { key: CompletionNoteSectionKey; content: string }[];
};

const OUTCOME: Record<
  Outcome,
  {
    title: string;
    body: string;
    cta: string;
    needsReason: boolean;
    // The reason field is shared, so the example has to say what THIS
    // outcome's justification looks like — a refusal reads nothing like
    // "why I want to see the patient" (TES-467).
    reasonPlaceholder?: string;
    // TES-734 — the patient reads this outcome's reason in the app.
    reasonPatientVisible: boolean;
  }
> = {
  approved: {
    title: "Godkänn receptövertag",
    body: "Ella tar över behandlingen. Ordinationen och receptstatusen kommer från Recept-sektionen; bekräfta behandlingsfasen och när behandlingen började — det är vad som aktiveras när patienten tecknat abonnemanget.",
    cta: "Godkänn receptövertag",
    needsReason: false,
    reasonPatientVisible: false,
  },
  cannot_transfer: {
    // TES-1243 — the five outcomes in A-02 2026-10-11's words (§8).
    title: "Ella kan inte ta över behandlingen",
    body: "Behandlingen avslutas hos Ella. Patienten läser din motivering och vart hon kan vända sig.",
    cta: "Bekräfta",
    needsReason: true,
    reasonPlaceholder:
      "T.ex. läkemedlet ingår inte i Ellas sortiment, så vi kan inte ta över behandlingen",
    reasonPatientVisible: true,
  },
  // TES-734 — "Nytt blodprov krävs". The paid checklist on the transfer
  // case follows in TES-741; the message is what she reads until then.
  labs_required: {
    title: "Nytt blodprov krävs",
    body: "Patienten får veta att ett nytt blodprov behövs innan övertaget kan bedömas. Ärendet står kvar hos dig.",
    cta: "Begär nytt blodprov",
    needsReason: true,
    reasonPlaceholder:
      "T.ex. vi behöver aktuella lever- och njurvärden innan övertaget",
    reasonPatientVisible: true,
  },
  // TES-762 — the fifth outcome. The care meeting is needs-driven, not a
  // mandatory step: the doctor chooses it when she cannot decide on paper.
  // The decision mints a 0 kr ConsultPayment so the patient can book without
  // paying, and after a documented meeting the SAME case comes back here with
  // all its material intact.
  meeting_required: {
    title: "Vårdmöte krävs",
    body: "Patienten får boka ett vårdmöte utan avgift. Ärendet står kvar hos dig och kommer tillbaka till granskning när du dokumenterat mötet — allt hon redan skickat in finns kvar.",
    cta: "Begär vårdmöte",
    needsReason: true,
    reasonPlaceholder:
      "T.ex. behandlingshistoriken behöver gås igenom innan jag kan ta över",
    reasonPatientVisible: true,
  },
  // TES-950 — the shared completion picker, and no free-text reason (Ö-243's
  // assumption (a)). The patient reads the standard line instead. TES-941:
  // "Kirill ska inte behöva skriva en fritextmotivering."
  completion_requested: {
    // TES-909 — Emelie 2026-09-24 named the dialog and its question:
    // "Dialogen ska heta 'Begär komplettering' och fråga 'Vad behöver patienten komplettera?'"
    // TES-1243 — A-02 2026-10-11 §8 names the outcome "Begär riktad
    // komplettering"; the newest decision wins.
    title: "Begär riktad komplettering",
    body: "Patienten får komplettera det du väljer — inget annat. Ärendet står kvar hos dig tills hon skickat in.",
    cta: "Begär riktad komplettering",
    needsReason: false,
    reasonPatientVisible: false,
  },
};

// TES-1243 — "De fem utfallen är: 1. Godkänn receptövertag 2. Begär riktad
// komplettering 3. Nytt blodprov krävs 4. Vårdmöte krävs 5. Ella kan inte ta
// över behandlingen" (TES-1240), in that order.
const OUTCOME_ORDER: readonly Outcome[] = [
  "approved",
  "completion_requested",
  "labs_required",
  "meeting_required",
  "cannot_transfer",
];

// TES-911/TES-909 — where a reading came from, in the doctor's words:
// measurementSourceLabel (contracts), shared with the monthly journal.

const PRECISION_LABEL: Record<TreatmentStartPrecision, string> = {
  exact: "Exakt datum",
  estimated: "Uppskattat",
};

// What the API's `{error}` codes mean to the doctor (CAR-62 preserves them).

// The v4 answers ("over_4_veckor") used to fall through to "not given" here.
function supplyLine(value: string | null): string {
  return value
    ? `Mängd kvar: ${remainingSupplyLabel(value)}`
    : "Mängd kvar ej angiven";
}

const DECIDE_ERRORS: Record<string, string> = {
  concurrent_transition:
    "Någon annan hann före — ärendet laddas om med det aktuella beslutet.",
  invalid_transition:
    "Beslutet är inte längre tillåtet i ärendets status — ärendet laddas om.",
  transfer_review_not_found: "Ärendet är redan avslutat — ärendet laddas om.",
};

// TES-994 — refusals the doctor can correct in the open dialog, so it stays
// open. Same wording as the API's `errors.clinician.*` entries. Without this
// the dialog printed the raw code (`evidence_already_supplied`).
const DECIDE_FIXABLE_ERRORS: Record<string, string> = {
  evidence_already_supplied:
    "Uppgiften finns redan i patientens underlag och kan inte begäras på nytt.",
  // TES-1034 S7 — the journal gate, as errors.clinician.transfer_journal_*.
  journal_not_in_webdoc:
    "Beslutet kan inte sparas utan journalanteckning. Kopiera journalanteckningen till Webdoc och bekräfta att den är införd.",
  journal_empty:
    "Journalanteckningen är tom. Skriv under rätt journalrubrik innan du fattar beslutet.",
  // TES-1071 — the link goes by e-mail only.
  email_not_verified:
    "Patienten saknar verifierad e-postadress. Abonnemangslänken skickas bara via e-post, så receptövertaget kan inte godkännas förrän hon har verifierat sin e-post.",
  // TES-1242 — as errors.clinician.transfer_planned_treatment_changed and
  // transfer_plan_change_reason_required.
  transfer_planned_treatment_changed:
    "Receptuppgifterna skiljer sig från den sparade planerade läkemedelsbehandlingen. Välj ”Ändra behandlingen” och ange den medicinska motiveringen, eller återställ uppgifterna.",
  transfer_plan_change_reason_required:
    "Ange den medicinska motiveringen till att behandlingen ändras.",
  positive_decision_not_documented:
    "Bekräfta att beslutet och den planerade ordinationen är dokumenterade.",
};

// TES-1031 — the decision dialog's fields in screen order: what the error
// summary names and where "scroll to the first invalid field" starts.
const FORM = "transfer-decision";
const DECISION_FIELDS: readonly FieldSpec[] = [
  { key: "completion", label: "Komplettering" },
  // TES-1241 — the order is the Recept section's, outside the dialog.
  { key: "recept", label: "Recept" },
  { key: "clinicalPhase", label: "Klinisk behandlingsfas" },
  { key: "startedAt", label: "Verifierat startdatum" },
  { key: "reason", label: "Motivering" },
  { key: "carePath", label: "Fortsatt vårdväg" },
  { key: "webdoc", label: "Kontroll i Webdoc" },
];

// Where the decision schema's (and the server's) refusal paths land.
const DECISION_PATHS: Record<string, string> = {
  completion: "completion",
  completionGroups: "completion",
  "approval.medication": "recept",
  "approval.dose": "recept",
  "approval.frequency": "recept",
  "approval.duration": "recept",
  "approval.strength": "recept",
  "approval.plan": "recept",
  "approval.plannedTreatment": "recept",
  "approval.planChangeReason": "recept",
  // TES-1242 — the plan saved with a komplettering.
  plannedMedication: "recept",
  "plannedMedication.medication": "recept",
  "plannedMedication.dose": "recept",
  "plannedMedication.frequency": "recept",
  "plannedMedication.duration": "recept",
  "plannedMedication.strength": "recept",
  "plannedMedication.plan": "recept",
  "plannedMedication.plannedTreatment": "recept",
  "plannedMedication.planChangeReason": "recept",
  "approval.clinicalPhase": "clinicalPhase",
  "approval.currentTreatmentStartedAt": "startedAt",
  "approval.treatmentStartPrecision": "startedAt",
  reason: "reason",
  carePath: "carePath",
  prescription: "webdoc",
  webdocControl: "webdoc",
};

function FieldErrorText({ message }: { message: string | undefined }) {
  return message ? <p className="text-xs text-danger">{message}</p> : null;
}

// TES-763 — phrased as a REQUEST, not as a state already reached.
// `MedicalEvidencePanel` has its own set ("Blodprovet betalt") that
// describes the checklist's status; here the doctor chooses what the patient
// should DO, and the same words would have read as if it were already done.
const REQUEST_TASK_LABEL: Record<MedicalEvidenceTaskKey, string> = {
  previous_lab_results: "Ladda upp tidigare provsvar",
  prescription_evidence: "Ladda upp recept-/ordinationsunderlag",
  lab_payment: "Beställ och betala blodprovet",
  new_blood_test: "Lämna blodprovet",
  // Ö-207 — the patient's checklist row is "Mät blodtryck och vilopuls".
  // Kirill picks the item here, so it must carry the same name she reads; two
  // names for one item is what Ö-207 exists to remove.
  blood_pressure_pulse: "Mät blodtryck och vilopuls",
};

export function TransferReviewPanel({
  patientId,
  review,
  transferSubmission,
  intakeSubmission,
  onUnauthorized,
  onDecided,
  onStale,
  awaitingSubscription = false,
}: {
  patientId: string;
  review: TransferReview;
  // The submissions the review binds (null only if the detail payload and
  // the review disagree, which the API prevents). Used for the dates and the
  // raw-answer fallbacks; the summaries themselves ride on `review`.
  transferSubmission: FormSubmission | null;
  intakeSubmission: FormSubmission | null;
  onUnauthorized: () => void;
  // The decision landed; the parent reloads the detail (journey, treatment
  // and this review all changed together).
  onDecided: (review: TransferReview) => void;
  // A decision lost to a concurrent one, or taken on a status that has
  // since moved: the caller reloads the patient so the doctor sees what
  // actually stands.
  onStale: () => void;
  // TES-1034 S5 — approved, first cycle not yet activated by her
  // subscription (the header's "Inväntar aktiverat abonnemang").
  awaitingSubscription?: boolean;
}) {
  // TES-1040 — the admin's read-only view: the case and the note are shown,
  // the decision buttons are not (and so the decision dialog never opens).
  const readOnly = useReadOnly();
  const [error, setError] = useState<string | null>(null);
  // TES-1056 — assessments the doctor changed in the evidence card above
  // the earlier readings, so their tags follow without a reload.
  const [reviewed, setReviewed] = useState<
    Record<string, MeasurementReviewStatus>
  >({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [openUrl, setOpenUrl] = useState<string | null>(null);
  const [openMime, setOpenMime] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  // TES-1030 — the answer a clicked flag points at, highlighted for a
  // moment after the page scrolls to it.
  const [highlight, setHighlight] = useState<{
    scope: "f07" | "f02";
    key: string;
  } | null>(null);
  useEffect(() => {
    if (!highlight) return;
    document
      .getElementById(answerAnchorId(highlight.scope, highlight.key))
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = window.setTimeout(() => setHighlight(null), 4000);
    return () => window.clearTimeout(t);
  }, [highlight]);
  // TES-1034 S7 — her raw answers are secondary now, collapsed under the
  // journal; a flag that links to one opens its column's list first.
  const [answersOpen, setAnswersOpen] = useState({ f07: false, f02: false });
  const openAnswer = (scope: "f07" | "f02", key: string) => {
    setAnswersOpen((o) => ({ ...o, [scope]: true }));
    setHighlight({ scope, key });
  };

  // TES-1034 S7 (Emelie, points 1-2) — the review's journal note, written
  // under Kirill's headings and prefilled from F-01, F-02B and F-07 with
  // their sources. Every decision below waits for the compiled note to be
  // copied to Webdoc and confirmed; the API refuses a decision without the
  // tick and stores the note's final version and receipt with it.
  const journalRef = useRef<JournalSectionsHandle>(null);
  // TES-1034 — the per-heading AI, when the AI switch is on; otherwise the
  // note is the deterministic prefill as before. TES-1294 — used only by the
  // auto-summary on "Sammanställ journalanteckning", no button per heading.
  const journalAi = useTransferJournalAi(patientId, onUnauthorized);
  const loadJournal = useCallback(
    () => fetchTransferReviewJournal(patientId),
    [patientId],
  );
  const saveJournal = useCallback(
    (input: UpdateJournalSections) =>
      saveTransferReviewJournal(patientId, input),
    [patientId],
  );
  // TES-1312 — the AI run before the note is composed, and the headings it
  // wrote.
  const [summaryProgress, setSummaryProgress] = useState<string | null>(null);
  const [aiHeadings, setAiHeadings] = useState<string[]>([]);
  const [webdocNote, setWebdocNote] = useState<{
    text: string;
    skipped: string[];
  } | null>(null);
  // The boxes as saved when the note was last compiled (TES-1063).
  const [webdocValues, setWebdocValues] = useState<JournalValues | null>(null);
  // TES-1262 — back after kompletteringar: the round's
  // Kompletteringsanteckning as stored when the note was last compiled.
  const [webdocCompletion, setWebdocCompletion] =
    useState<CompletionNoteTarget | null>(null);
  const [journalDone, setJournalDone] = useState(false);
  const [noteChanged, setNoteChanged] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const noteRef = useRef<HTMLTextAreaElement>(null);

  // TES-1034 S8 — the price per plan for her choice's label: the tenant's
  // pricing, as the link preview reads it. Without it the labels show the
  // option's name only.
  const [planAmounts, setPlanAmounts] = useState<PlanAmounts>({});
  useEffect(() => {
    let live = true;
    fetchSubscriptionPlans()
      .then(({ plans }) => {
        if (!live) return;
        setPlanAmounts(
          Object.fromEntries(plans.map((p) => [p.plan, p.amountMinor])),
        );
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  // The decision being confirmed. `reason` is required for everything but
  // an approval — the API enforces the same rule.
  const [pending, setPending] = useState<Outcome | null>(null);
  const [reason, setReason] = useState("");
  // TES-950 — the shared completion picker. What the items become (F-07
  // sections, CP-06 tasks) is resolved on the server and never shown to him:
  // "F-07 och CP-06 får förekomma som interna källidentifierare, men inte som handlingsspråk för Kirill eller patienten."
  const [completion, setCompletion] =
    useState<CompletionValue>(COMPLETION_START);
  // TES-763 — the CP-06 tasks the doctor explicitly picks. Kept for
  // `labs_required`, which is not a komplettering and has its own rule: an
  // empty selection derives the tasks from the case type.
  const [tasks, setTasks] = useState<MedicalEvidenceTaskKey[]>([]);
  const [busy, setBusy] = useState(false);
  // TES-1241 (TES-1240) — the Recept section: the planned medication,
  // prefilled from the current plan or her F-07 answers, and the two choices.
  // The approval sends what stands here; the dialog never asks again.
  const [planned, setPlanned] = useState<PlannedMedicationView | null>(null);
  const [recept, setRecept] = useState<TransferReceptValue>(() =>
    initialRecept(review.reportedTreatment, null),
  );
  const [receptAttempted, setReceptAttempted] = useState(false);
  // A ref, read when the plan arrives rather than when the request left.
  const receptTouched = useRef(false);
  useEffect(() => {
    let live = true;
    fetchPlannedMedication(patientId)
      .then((v) => {
        if (!live) return;
        setPlanned(v);
        // A plan from a care meeting on this case is newer than F-07; it is
        // the start unless the doctor has already begun in the section.
        if (v.current && !receptTouched.current) {
          setRecept(initialRecept(review.reportedTreatment, v.current));
        }
      })
      .catch((e) => {
        if (live && e instanceof UnauthorizedError) onUnauthorized();
      });
    return () => {
      live = false;
    };
    // onUnauthorized is a fresh callback each render; the plan is per patient.
  }, [patientId]);
  // TES-1242 — back after kompletteringar, the same case shows the round's
  // Kompletteringsanteckning (the earlier note stays locked history above)
  // with its own Webdoc confirmation, as on the Blodprov tab.
  const askedBefore = Boolean(
    review.completionRequestedAt || review.labsRequestedAt,
  );
  const [noteRoundId, setNoteRoundId] = useState<string | null>(null);
  // TES-1262 (TES-1061 parity) — the round the decision is journaled in,
  // picked as the API picks it: its Kompletteringsanteckning is the note the
  // Webdoc step compiles, not the review's own (earlier, locked) note.
  const [decisionRoundId, setDecisionRoundId] = useState<string | null>(null);
  useEffect(() => {
    if (!askedBefore || readOnly) return;
    let live = true;
    fetchMedicalEvidence(patientId)
      .then((d) => {
        if (!live) return;
        const decided =
          transferDecisionRound(d.rounds ?? [], review.id)?.id ?? null;
        setDecisionRoundId(decided);
        setNoteRoundId(decided ?? completionNoteRoundId(d));
      })
      .catch((e) => {
        if (live && e instanceof UnauthorizedError) onUnauthorized();
      });
    return () => {
      live = false;
    };
    // onUnauthorized is a fresh callback each render.
  }, [patientId, review.id, askedBefore, readOnly]);
  const onReceptChange = (next: TransferReceptValue) => {
    receptTouched.current = true;
    setRecept(next);
  };
  const order = recept.order;
  // TES-1242 — back after kompletteringar with a plan saved (a care meeting,
  // or this review's own komplettering), the choice reads "Behandlingen
  // gäller fortfarande" / "Ändra behandlingen".
  const returning = receptReturning(review, planned?.current);
  const planLabels = receptPlanChoiceLabels(returning);
  const receptErrors = {
    ...receptChoiceProblems(recept, { returning }),
    ...orderFieldErrors(order),
  };
  const receptIncomplete = Object.values(receptErrors).some(Boolean);
  // TES-1242 — with a komplettering or a new blood test the plan is saved
  // when the doctor has made the plan choice; no prescription status.
  const completionPlan = receptPlannedMedication(recept);
  const completionReceptErrors = completionPlan
    ? {
        ...receptChoiceProblems(recept, {
          returning,
          needPrescription: false,
        }),
        ...orderFieldErrors(order),
      }
    : {};
  const completionReceptIncomplete = Object.values(completionReceptErrors).some(
    Boolean,
  );
  const asksForEvidence =
    pending === "completion_requested" || pending === "labs_required";
  // The one prescription choice the dialog's Webdoc step offers.
  const [decisionChoices, setDecisionChoices] =
    useState<readonly DecisionPrescriptionChoice[]>(DECISION_CHOICES);
  const prescriptionDocuments = review.uploads.filter(
    (u) => u.evidenceReview,
  ).length;
  // TES-841 — no preselected phase: "Inget defaultvärde."
  const [phase, setPhase] = useState<ClinicalPhase | null>(null);
  // Prefilled from her own answer; the doctor verifies or corrects it.
  const [startedAt, setStartedAt] = useState(
    review.selfReportedTreatmentStartedAt ?? "",
  );
  const [precision, setPrecision] = useState<TreatmentStartPrecision>(
    review.selfReportedTreatmentStartedAt ? "estimated" : "exact",
  );
  const [carePath, setCarePath] = useState("");
  // TES-1071 (TES-1069 point 3) — no prescription choice at the decision.
  // An approval asks A-02 Steg B's two further ticks beside the journal
  // tick; the prescription is confirmed after payment.
  // TES-1187 (TES-1180 point 9) — an approval carries its prescription
  // status, as a new treatment's decision does (TES-1185): "Receptet är
  // utfärdat i Webdoc" or "Befintligt recept gäller fortfarande", in place
  // of TES-1071's Steg B ticks. Nothing preselected.
  const [prescription, setPrescription] = useState<PrescriptionChoiceValue>(
    EMPTY_PRESCRIPTION_CHOICE,
  );
  // TES-1187 / TES-1290 — the approval's "Läkarens kommentar till
  // behandlingsbeslutet", prefilled. Null until the doctor types in it.
  const [notice, setNotice] = useState<string | null>(null);
  const noticePrefill = treatmentCommentPrefill("transfer_approved");
  const noticeText = doctorNoticeText(notice, noticePrefill);
  const publishedNotice = finalizeTreatmentComment(noticeText);
  const prescriptionPayload = prescriptionDecisionPayload(prescription);
  const approvalPrescription =
    prescriptionPayload && prescriptionPayload.choice !== "not_yet"
      ? {
          choice: prescriptionPayload.choice,
          ...(prescriptionPayload.existingValidUntil
            ? { existingValidUntil: prescriptionPayload.existingValidUntil }
            : {}),
        }
      : null;
  const [labOrderDone, setLabOrderDone] = useState(false);
  // TES-442 — "Remissnummer i Webdoc (valfritt)" beside the remiss tick.
  const [requisitionRef, setRequisitionRef] = useState("");

  // The order the note's planned medication is written from: only an approval
  // prescribes, the same rule the API compiles the stored version with.
  // TES-1242 — a komplettering with a plan writes it as "Planerad
  // läkemedelsbehandling" instead (the decision is pending).
  const journalOrder =
    pending === "approved" || (asksForEvidence && completionPlan)
      ? transferJournalOrder({
          ...toOrder(order),
          strength: recept.strength,
          plan: recept.plan,
        })
      : null;

  // Compiles the note exactly as the API will store it.
  function noteFrom(
    values: JournalValues,
    completionNote: CompletionNoteTarget | null = webdocCompletion,
  ): {
    text: string;
    skipped: string[];
  } {
    const sections = TRANSFER_JOURNAL_LAYOUT.keys.map((key) => ({
      key,
      content: values[key] ?? "",
    }));
    // TES-1058 (Emelie, point A) — the decision in the dialog, as the API
    // compiles it from the same fields it is sent.
    const opts = {
      order: journalOrder,
      decision: pending
        ? transferJournalDecision({
            outcome: pending,
            reason: OUTCOME[pending].needsReason ? reason.trim() : undefined,
            carePath:
              pending === "cannot_transfer" ? carePath.trim() : undefined,
            completion:
              pending === "completion_requested"
                ? completionPayload(completion)
                : undefined,
            // TES-1187 — the prescription status, journaled with the approval.
            prescription:
              pending === "approved" ? approvalPrescription : undefined,
          })
        : null,
    };
    // TES-1262 — back after kompletteringar, the decision goes into the
    // round's Kompletteringsanteckning instead, as the API compiles it.
    // TES-1400 (Ö-455) — with the order under "Åtgärd och behandlingsplan",
    // as in the review's own note.
    if (completionNote) {
      return {
        text: assembleCompletionNoteText(
          completionNote.noteDate,
          completionNote.sections,
          opts.decision,
          opts.order,
        ),
        skipped: emptyJournalHeadingsFor(
          COMPLETION_NOTE_LAYOUT,
          completionNote.sections,
          opts,
        ),
      };
    }
    return {
      text: assembleJournalTextFor(TRANSFER_JOURNAL_LAYOUT, sections, opts),
      skipped: emptyJournalHeadingsFor(TRANSFER_JOURNAL_LAYOUT, sections, opts),
    };
  }

  // Saves what is unsaved in the boxes, then compiles the note from them.
  // The saved boxes are kept, so the note can follow the dialog's other
  // inputs without saving again.
  async function compileNote(): Promise<{ text: string; skipped: string[] }> {
    const values = (await journalRef.current?.flush()) ?? {};
    // TES-1262 — the Kompletteringsanteckning as saved now, read afresh so
    // the step shows what the API will store.
    const note = decisionRoundId
      ? await fetchCompletionNote(patientId, decisionRoundId)
      : null;
    const completionNote: CompletionNoteTarget | null = note
      ? {
          noteDate: note.noteDate,
          sections: note.sections.map((s) => ({
            key: s.key,
            content: s.content,
          })),
        }
      : null;
    setWebdocValues(values);
    setWebdocCompletion(completionNote);
    return noteFrom(values, completionNote);
  }

  // TES-1063 — the note as it stands now, from the boxes saved when the step
  // compiled it and the dialog's inputs (the prescription status, the order,
  // the reason, the care path, the komplettering). When it moves away from
  // the note shown, the step shows the new text at once and, if the old one
  // was already copied or ticked, takes the tick back and says so — instead
  // of the doctor finding out only when she confirms.
  const liveNote = webdocValues ? noteFrom(webdocValues) : null;
  const liveText = liveNote?.text ?? null;
  useEffect(() => {
    if (!liveNote) return;
    const change = noteInputChanged(webdocNote?.text ?? null, liveNote.text, {
      journalDone,
      copied: copyState !== "idle",
    });
    if (!change) return;
    setWebdocNote(liveNote);
    if (change.reconfirm) {
      setJournalDone(false);
      setCopyState("idle");
      setNoteChanged(true);
    }
    // Re-run only when the text itself changes; the ticks are read as they
    // are. (react-hooks/exhaustive-deps is not enabled in apps/admin.)
  }, [liveText]);

  // TES-1312 — headings still holding her raw answers are summarised by the
  // AI first; if any stays raw the card says so and nothing is composed
  // until the doctor chooses ("Sammanställ ändå" comes back with `force`).
  async function openWebdocStep(force = false) {
    setBusy(true);
    try {
      if (!force) {
        const gate = await journalRef.current?.summariseRaw((current, total) =>
          setSummaryProgress(autoSummaryProgressLabel(current, total)),
        );
        setSummaryProgress(null);
        if (gate && gate.raw.length > 0) return;
      }
      setWebdocNote(await compileNote());
      setAiHeadings(journalRef.current?.aiSummarisedHeadings() ?? []);
      setJournalDone(false);
      setCopyState("idle");
      setNoteChanged(false);
    } catch {
      // The card says why the save failed; the step stays closed.
    } finally {
      setSummaryProgress(null);
      setBusy(false);
    }
  }

  // "Kopiera hela journalanteckningen". Without clipboard access the text is
  // selected instead, for the doctor to copy by hand.
  async function copyNote() {
    if (!webdocNote?.text) return;
    try {
      await navigator.clipboard.writeText(webdocNote.text);
      setCopyState("copied");
    } catch {
      noteRef.current?.focus();
      noteRef.current?.select();
      setCopyState("failed");
    }
  }

  // A transfer review orders no blood test from here, so no remiss row.
  const journalBlocker = renewalWebdocBlocker({
    note: webdocNote?.text ?? null,
    journalDone,
    needsLabOrder: false,
    labOrderDone: false,
  });
  // TES-1031 — errors show at their fields once the doctor has tried to
  // confirm; refusals from the schema or the server show until she edits.
  const [attempted, setAttempted] = useState(false);
  const [issueErrors, setIssueErrors] = useState<Record<string, string>>({});
  const [focusTick, setFocusTick] = useState(0);

  // The previous object URL is released whenever a new one replaces it (the
  // cleanup runs with the old closure) and on unmount.
  useEffect(() => {
    return () => {
      if (openUrl) URL.revokeObjectURL(openUrl);
    };
  }, [openUrl]);

  async function view(upload: TransferReviewUpload) {
    if (openId === upload.id) {
      setOpenId(null);
      setOpenUrl(null);
      setOpenMime(null);
      return;
    }
    setLoadingId(upload.id);
    setError(null);
    try {
      const blob = await fetchPatientUploadBlob(patientId, upload.id);
      setOpenId(upload.id);
      setOpenUrl(URL.createObjectURL(blob));
      setOpenMime(upload.mimeType);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingId(null);
    }
  }

  function closeModal() {
    setPending(null);
    setReason("");
    setCompletion(COMPLETION_START);
    setTasks([]);
    setCarePath("");
    setPrescription(EMPTY_PRESCRIPTION_CHOICE);
    setDecisionChoices(DECISION_CHOICES);
    setNotice(null);
    setLabOrderDone(false);
    setWebdocNote(null);
    setWebdocValues(null);
    setWebdocCompletion(null);
    setJournalDone(false);
    setNoteChanged(false);
    setCopyState("idle");
    setError(null);
    setAttempted(false);
    setIssueErrors({});
  }

  // Refusals from the schema or the server, marked at their fields. Anything
  // the dialog does not show becomes the dialog's own error line.
  function showIssues(issues: readonly ServerIssue[]) {
    const { messages, unmapped } = mapServerIssues(
      issues,
      DECISION_PATHS,
      (key) => localErrors[key] ?? null,
    );
    setIssueErrors(messages);
    setAttempted(true);
    setFocusTick((t) => t + 1);
    setError(
      unmapped.length || !Object.keys(messages).length
        ? "Beslutet kunde inte sparas. Kontrollera uppgifterna och försök igen."
        : null,
    );
  }

  async function confirm() {
    if (!pending) return;
    const spec = OUTCOME[pending];
    // TES-1031 — refuse with every invalid field marked, and the first one
    // scrolled to and focused, instead of one line naming the first problem.
    if (Object.values(localErrors).some(Boolean)) {
      setAttempted(true);
      // TES-1242 — and the Recept section shows what is missing.
      if (localErrors.recept) setReceptAttempted(true);
      setFocusTick((t) => t + 1);
      return;
    }
    // TES-1034 S7 — the note the doctor confirmed must still be the note as
    // it stands (a box edited, or the order changed, since the step compiled
    // it): otherwise it is shown again and must be confirmed again. Since
    // TES-1063 the step does this as soon as an input in the dialog
    // changes; this backstop catches a journal box edited after compiling.
    let current: { text: string; skipped: string[] };
    try {
      current = await compileNote();
    } catch {
      return;
    }
    if (!confirmedNoteStillCurrent(webdocNote?.text ?? null, current.text)) {
      setWebdocNote(current);
      setJournalDone(false);
      setCopyState("idle");
      setNoteChanged(true);
      return;
    }
    const webdocControl = {
      ...transferWebdocControl({
        journalDone,
        needsLabOrder,
        labOrderDone,
        requisitionRef,
      }),
    };
    // The same schema the API validates with, so the modal refuses exactly
    // what the server would.
    const parsed = transferReviewDecisionSchema.safeParse({
      outcome: pending,
      reason: spec.needsReason ? reason.trim() : undefined,
      // TES-950 — the picker's items; the server resolves them into the two
      // internal vocabularies.
      completion:
        pending === "completion_requested"
          ? completionPayload(completion)
          : undefined,
      // TES-763 — exhaustive when set: only what is listed here happens, and
      // above all no test payment she did not ask for.
      evidenceTasks: pending === "labs_required" ? tasks : undefined,
      // TES-1242 — the plan, saved with the komplettering.
      plannedMedication:
        asksForEvidence && completionPlan ? completionPlan : undefined,
      carePath: pending === "cannot_transfer" ? carePath.trim() : undefined,
      // TES-1187 — the prescription status and the doctor's notice, on an
      // approval.
      prescription:
        pending === "approved"
          ? (approvalPrescription ?? undefined)
          : undefined,
      patientNotice:
        pending === "approved" && publishedNotice ? publishedNotice : undefined,
      // TES-997 — the remiss confirmation, whenever a new sample is asked.
      // TES-1034 S7 — and the journal note's, on every decision.
      webdocControl,
      approval:
        pending === "approved"
          ? {
              ...toOrder(order),
              // TES-1241 — the rest of the Recept section's planned
              // medication, saved with the approval.
              ...receptApprovalParts(recept),
              // Left undefined when unchosen, so the shared decision schema
              // refuses the decision instead of the panel inventing a phase.
              clinicalPhase: phase ?? undefined,
              currentTreatmentStartedAt: startedAt,
              treatmentStartPrecision: precision,
            }
          : undefined,
    });
    if (!parsed.success) {
      // The dialog's own checks above should catch everything the schema
      // refuses; this is the backstop, mapped onto the same fields.
      showIssues(
        parsed.error.issues.map((i) => ({
          path: i.path.join("."),
          message: i.message,
        })),
      );
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const updated = await decideTransferReview(patientId, parsed.data);
      closeModal();
      onDecided(updated);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      if (e instanceof ValidationError) {
        showIssues(e.issues);
        return;
      }
      const code = e instanceof Error ? e.message : String(e);
      const stale = DECIDE_ERRORS[code];
      setError(stale ?? DECIDE_FIXABLE_ERRORS[code] ?? code);
      if (stale) {
        // Leave the message readable, then show what actually stands.
        window.setTimeout(() => {
          closeModal();
          onStale();
        }, 1500);
      }
    } finally {
      setBusy(false);
    }
  }

  // TES-1241 — "Godkänn receptövertag" starts from the Recept section: an
  // incomplete one is shown where it is filled in, not inside the dialog.
  // The dialog's prescription status follows the section's choice.
  // TES-1243 — choosing an outcome opens its fields below, in the workspace.
  // A new choice starts them afresh, as reopening the dialog used to.
  function chooseOutcome(o: Outcome) {
    closeModal();
    // TES-763 — "Nytt blodprov krävs" MEANS the two sub-steps, so they are
    // pre-checked. The default is visible and can be unticked: what she sees
    // is what she sends.
    if (o === "labs_required") setTasks(["lab_payment", "new_blood_test"]);
    // An approval rests on the Recept section: show what it still lacks.
    if (o === "approved" && (receptIncomplete || !recept.need)) {
      setReceptAttempted(true);
    }
    setPending(o);
  }

  // TES-1241 — the approval's prescription status follows the Recept
  // section's choice, also when it is made after the outcome.
  useEffect(() => {
    if (pending !== "approved") return;
    if (!recept.need) {
      setDecisionChoices(DECISION_CHOICES);
      return;
    }
    const preset = receptPrescriptionPreset(
      recept.need,
      review.reportedTreatment,
    );
    setPrescription({
      choice: preset.choice,
      existingValidUntil: preset.existingValidUntil,
    });
    setDecisionChoices(preset.choices);
    // The reported date is fixed for the review.
  }, [pending, recept.need]);

  function toggleTask(task: MedicalEvidenceTaskKey) {
    setTasks((prev) =>
      prev.includes(task) ? prev.filter((t) => t !== task) : [...prev, task],
    );
  }

  // The remiss line of the control step: "Nytt blodprov krävs", or the
  // picker's "Ta ett nytt blodprov" on a komplettering.
  const needsLabOrder =
    pending === "labs_required" ||
    (pending === "completion_requested" &&
      completion.items.includes("new_blood_test"));

  // TES-1031 — what each field says while the decision cannot be sent.
  const localErrors: Record<string, string | undefined> = (() => {
    const e: Record<string, string | undefined> = {};
    if (!pending) return e;
    if (pending === "completion_requested") {
      const p = completionProblem(completion);
      if (p) e.completion = p;
    }
    if (pending === "approved") {
      // TES-1241 — the order is checked in the Recept section; the dialog
      // opens only once it is complete, so this is the backstop.
      if (receptIncomplete) e.recept = "Slutför Recept-sektionen.";
      if (!phase) e.clinicalPhase = "Välj klinisk behandlingsfas.";
      if (!startedAt) e.startedAt = "Ange verifierat startdatum.";
      if (!approvalPrescription) e.webdoc = "Välj receptstatus.";
    }
    // TES-1242 — a plan choice made in the Recept section is saved with the
    // komplettering, so it must be complete; none made sends none.
    if (asksForEvidence && completionReceptIncomplete) {
      e.recept = "Slutför Recept-sektionen.";
    }
    if (OUTCOME[pending].needsReason && !reason.trim())
      e.reason = "Ange en motivering.";
    if (pending === "cannot_transfer" && !carePath.trim())
      e.carePath = "Ange fortsatt vårdväg.";
    if (needsLabOrder && !labOrderDone)
      e.webdoc = "Bekräfta att provbeställningen/remissen är klar i Webdoc.";
    // TES-1034 S7 — the journal note first: compiled, copied, confirmed.
    if (journalBlocker) e.webdoc = journalBlocker;
    return e;
  })();
  const fieldErrors = orderedFieldErrors(DECISION_FIELDS, {
    ...(attempted ? localErrors : {}),
    ...issueErrors,
  });
  const shownError = (key: string) =>
    fieldErrors.find((f) => f.key === key)?.message;
  const summaryLine = errorSummary(fieldErrors);

  // Any edit clears the refusals: they described the request that was
  // checked, not the dialog as it stands now.
  useEffect(() => {
    setIssueErrors({});
  }, [
    recept,
    phase,
    startedAt,
    precision,
    reason,
    carePath,
    completion,
    prescription,
    notice,
    labOrderDone,
    journalDone,
  ]);

  // Scroll to and focus the first invalid field after the render that
  // shows the errors.
  useEffect(() => {
    if (focusTick === 0) return;
    focusFirstInvalid(fieldErrors, (key) =>
      findFieldElement(document, FORM, key),
    );
    // Runs once per request, not on every change of the error list.
  }, [focusTick]);

  const status = transferReviewStatusLabel(review.status, {
    awaitingSubscription,
  });
  const isOpen = (OPEN_TRANSFER_REVIEW_STATUSES as readonly string[]).includes(
    review.status,
  );
  const can = (outcome: Outcome) =>
    canTransitionTransferReview(review.status, outcome);

  const renderUploads = (uploads: TransferReviewUpload[]) => (
    <ul className="flex flex-col gap-xs text-sm mt-xs">
      {uploads.map((u) => (
        <li
          key={u.id}
          className="border-b border-surfaceMuted last:border-b-0 py-xs"
        >
          <button
            type="button"
            onClick={() => void view(u)}
            className="flex w-full items-center justify-between gap-md text-left"
          >
            <span>
              {u.fieldLabel ? (
                <span className="block text-xs text-textSecondary">
                  {u.fieldLabel}
                </span>
              ) : null}
              <span className="text-brand underline">
                {openId === u.id ? "Dölj" : "Visa"} {u.filename}
              </span>
              <span className="text-textSecondary text-xs ml-sm">
                {u.mimeType} • {Math.round(u.byteSize / 1024)} kB
              </span>
            </span>
            <span className="text-textSecondary text-xs">
              {loadingId === u.id ? "Hämtar…" : fmt(u.createdAt)}
            </span>
          </button>
          {/* TES-1057 / TES-1049 — the review of a prescription document. */}
          {u.evidenceReview ? (
            <PrescriptionEvidenceReviewControl
              patientId={patientId}
              review={u.evidenceReview}
              canEdit={isOpen}
              onSaved={onDecided}
              onUnauthorized={onUnauthorized}
            />
          ) : null}
          {openId === u.id && openUrl ? (
            openMime === "application/pdf" ? (
              <iframe
                src={openUrl}
                title={u.filename}
                className="mt-sm w-full h-[640px] rounded-sm border border-surfaceMuted"
              />
            ) : (
              <img
                src={openUrl}
                alt={u.filename}
                className="mt-sm max-h-[480px] rounded-sm border border-surfaceMuted"
              />
            )
          ) : null}
        </li>
      ))}
    </ul>
  );

  const rawAnswers = (
    title: string,
    submission: FormSubmission | null,
    version: number | null,
  ) =>
    submission ? (
      <details className="mt-md">
        <summary className="cursor-pointer text-sm text-textSecondary">
          Alla svar (rådata)
        </summary>
        <p className="text-xs text-textSecondary mt-sm mb-sm">
          {title} v{version ?? submission.templateVersion} — inlämnad{" "}
          {fmt(submission.submittedAt)} • språk{" "}
          {submission.submittedInLocale.toUpperCase()}
        </p>
        <pre className="text-xs bg-surfaceMuted/40 p-sm rounded-sm overflow-x-auto">
          {JSON.stringify(submission.answers, null, 2)}
        </pre>
      </details>
    ) : null;

  const columnHeader = (
    form: string,
    version: number | null,
    submission: FormSubmission | null,
  ) => (
    <h3 className="font-display text-base mb-sm">
      {form}
      <span className="text-textSecondary text-xs font-sans ml-sm">
        {version !== null ? `v${version}` : "version okänd"}
        {submission ? ` • inskickad ${fmt(submission.submittedAt)}` : ""}
      </span>
    </h3>
  );

  // TES-1241 — the Recept section's plan in the decision dialog, read-only:
  // what the approval saves (with the prescription status), or (TES-1242)
  // what a komplettering saves, never prescribed.
  const receptSummary = (withCompletion: boolean) => (
    <div
      id={fieldDomId(FORM, "recept")}
      tabIndex={-1}
      className="rounded-md bg-surfaceMuted/40 p-sm text-sm"
      data-testid="transfer-approve-recept"
    >
      <p className="text-xs font-semibold text-textSecondary">
        Planerad läkemedelsbehandling
        {recept.planChoice ? ` · ${planLabels[recept.planChoice]}` : ""}
      </p>
      <p className="font-semibold">
        {prescriptionLabel({
          medication: order.medication.trim(),
          dose: order.dose.trim(),
          frequency: order.frequency.trim(),
          duration: order.duration.trim() || null,
        })}
      </p>
      {recept.strength.trim() ? (
        <p>
          {PLANNED_MEDICATION_FIELD_LABEL.strength}: {recept.strength.trim()}
        </p>
      ) : null}
      {recept.plan.trim() ? (
        <p className="whitespace-pre-wrap">
          {PLANNED_MEDICATION_FIELD_LABEL.plan}: {recept.plan.trim()}
        </p>
      ) : null}
      {withCompletion ? (
        <p className="mt-xs text-xs text-textSecondary">
          {TRANSFER_RECEPT_SAVED_WITH_COMPLETION}
        </p>
      ) : recept.need ? (
        <p className="text-textSecondary">
          {TRANSFER_RECEPT_PRESCRIPTION_NEED_LABEL[recept.need]}
        </p>
      ) : null}
      <button
        type="button"
        className="mt-xs block text-xs text-brand underline"
        onClick={() => {
          const section = document.getElementById(RECEPT_SECTION_ID);
          section?.scrollIntoView({ behavior: "smooth", block: "start" });
          section?.focus({ preventScroll: true });
        }}
      >
        Ändra i Recept-sektionen.
      </button>
      <FieldErrorText message={shownError("recept")} />
    </div>
  );

  const missingSummary = (form: string) => (
    <p className="text-sm text-textSecondary">
      Formulärversionen för {form} kunde inte läsas — se rådata nedan.
    </p>
  );

  return (
    <section className="bg-surface border border-warning/40 rounded-md p-md">
      <div className="flex items-baseline justify-between gap-md mb-sm">
        <h2 className="font-display text-lg">Receptövertag</h2>
        <span
          className={`text-xs font-sans font-medium uppercase tracking-wide rounded border px-xs py-[2px] ${status.className}`}
        >
          {status.label}
        </span>
      </div>
      <p className="text-xs text-textSecondary mb-sm">
        Ärende öppnat {fmt(review.createdAt)}
        {review.decidedAt
          ? ` • bedömt ${fmt(review.decidedAt)}${
              review.decidedBy ? ` av ${review.decidedBy.name}` : ""
            }`
          : ""}
      </p>
      {review.requestedCompletionGroups.length > 0 ? (
        <p className="text-sm mb-sm">
          <span className="text-textSecondary">Komplettering begärd</span>
          {review.completionRequestedAt
            ? ` ${fmt(review.completionRequestedAt)}`
            : ""}
          :{" "}
          {review.requestedCompletionGroups
            .map(completionGroupLabel)
            .join(", ")}
        </p>
      ) : null}
      {review.waiverConsultPaymentId ? (
        <p className="text-sm text-textSecondary mb-sm">
          Avgiftsfritt vårdmöte upplåst — patienten bokar själv i appen.
        </p>
      ) : null}
      {review.reason ? (
        <p className="text-sm mb-md border-l-2 border-surfaceMuted pl-sm">
          {review.reason}
        </p>
      ) : null}
      {error ? <p className="text-danger text-sm mb-sm">{error}</p> : null}

      {/* TES-1034 S7 (Emelie, point 1) — the journal, first: editable under
          Kirill's headings, prefilled from F-01, F-02B and F-07, each line's
          original answer under "Källa". "Åtgärd och behandlingsplan" is
          generated from the order he approves (TES-1328). */}
      {/* TES-1034 S8 (Emelie, points 5-6) — her subscription choice, apart
          from the medical case: shown, never chosen or approved by him, and
          correctable only as an exception before the decision. */}
      {review.planChoice ? (
        <TransferPlanChoiceCard
          patientId={patientId}
          view={review.planChoice}
          amounts={planAmounts}
          canCorrect={isOpen}
          onCorrected={onDecided}
          onUnauthorized={onUnauthorized}
        />
      ) : null}

      {/* TES-1243 (A-02 2026-10-11 §3) — the workspace in Emelie's order:
          1-2 the overview, flags, missing evidence and both underlag with
          source and submission time; 3 the journal; 4 Recept; 5 the
          komplettering (and its note when the case is back); 6-10 the
          decision, the notice, "Sammanställ journalanteckning" once at the
          bottom, and the Webdoc confirmations. */}
      <div className="grid gap-lg grid-cols-1 min-[1100px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] mb-lg">
        {/* F-07 — what she is on, her evidence, the reading. */}
        <div className="min-w-0">
          {columnHeader(
            "F-07 Receptövertag",
            review.templateVersions.transfer,
            transferSubmission,
          )}
          <div className="flex flex-col gap-md">
            {/* TES-1029 — her attachments first, all in one place. They used
                to sit as plain rows inside the answer list under their
                section; on prod no staff member had ever opened one. */}
            <div className="rounded-md border border-surfaceMuted p-sm">
              <h3 className="text-xs font-semibold text-textSecondary uppercase tracking-wide mb-xs">
                {patientAttachmentsHeading(review.uploads.length)}
              </h3>
              {review.uploads.length > 0 ? (
                renderUploads(review.uploads)
              ) : (
                <p className="text-sm text-textSecondary">Inga bilagor.</p>
              )}
            </div>
            {/* The reading first — it is the one number set the doctor checks
                before anything else. */}
            <div>
              <h3 className="text-xs font-semibold text-textSecondary uppercase tracking-wide mb-xs">
                Registrerad mätning
              </h3>
              {review.reading ? (
                <p className="text-sm">
                  {review.reading.systolic ?? "—"}/
                  {review.reading.diastolic ?? "—"} mmHg •{" "}
                  {review.reading.pulse ?? "—"} slag/min
                  <span className="text-textSecondary text-xs ml-sm">
                    {fmt(review.reading.measuredAt)}
                  </span>
                </p>
              ) : (
                <p className="text-sm text-textSecondary">
                  Ingen ny mätning — se hennes svar under Blodtryck och puls.
                </p>
              )}
            </div>

            {/* TES-911 (Ö-235) — the evidence already on file, so the doctor
                can reuse it instead of asking for it again.

                Emelie 2026-09-24 declined to set a staleness threshold:
                "Systemet får gärna visa det senaste underlaget tydligt och kronologiskt, men ska inte fatta det kliniska beslutet åt läkaren."

                So no tag judges age: no "gammal"-badge and no colour for an
                old result — either would be the threshold she declined,
                wearing a different hat. A known age is a fact, tagged as
                complete; whether it is too old is his call.

                TES-1037 — the status of each piece of evidence is the same
                tag list as the overview tab's card (evidenceStatusRows +
                StatusTag), with its rule that no uploaded lab result is ONE
                missing item, not three. Emelie:
                "Vi ska inte blanda taggar på vissa rader med vanlig brödtext på andra."
                What stays below it is data, not status: the earlier readings
                with their dates and sources. */}
            <EvidenceSummaryCard
              patientId={patientId}
              onUnauthorized={onUnauthorized}
              embedded
              onReviewChanged={(sessionId, status) =>
                setReviewed((prev) => ({ ...prev, [sessionId]: status }))
              }
            />
            {review.existingEvidence.readings.length > 0 ? (
              <div>
                <h3 className="text-xs font-semibold text-textSecondary uppercase tracking-wide mb-xs">
                  Tidigare mätningar
                </h3>
                <ul className="flex flex-col gap-xs">
                  {review.existingEvidence.readings.map((r, i) => (
                    <li
                      key={`${r.measuredAt}-${i}`}
                      className="text-sm flex flex-wrap items-baseline gap-x-sm"
                    >
                      <span>
                        {r.systolic !== null
                          ? `${r.systolic}/${r.diastolic ?? "—"} mmHg`
                          : null}
                        {r.systolic !== null && r.pulse !== null ? " • " : ""}
                        {r.pulse !== null ? `${r.pulse} slag/min` : null}
                      </span>
                      <span className="text-textSecondary text-xs">
                        {fmt(r.measuredAt)} • {measurementSourceLabel(r)}
                      </span>
                      {r.fromThisReview ? (
                        <span className="text-xs text-brand">
                          inkom med den här ansökan
                        </span>
                      ) : null}
                      {/* TES-1056 — her reading's assessment, as a tag; it
                          is set on the newest reading in the card above, or
                          on any reading in the patient's measurement list. */}
                      {r.source !== "clinician" && r.sessionId ? (
                        <MeasurementReviewTag
                          status={
                            reviewed[r.sessionId] ??
                            r.reviewStatus ??
                            "patient_reported"
                          }
                        />
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {/* TES-728 — risk of a treatment gap, derived from next dose,
                remaining supply and prescription validity. Ordering signal
                for the doctor; never shown to the patient. */}
            {review.gapRisk ? (
              <div>
                <h3 className="text-xs font-semibold text-textSecondary uppercase tracking-wide mb-xs">
                  Risk för behandlingsglapp
                </h3>
                <p className="text-sm">
                  <span
                    className={`text-xs font-sans font-medium uppercase tracking-wide rounded border px-xs py-[2px] mr-sm ${GAP_RISK_BADGE[review.gapRisk.level].className}`}
                  >
                    {GAP_RISK_BADGE[review.gapRisk.level].label}
                  </span>
                  {/* TES-761 — the next dose is no longer asked for (F-07
                      v4); the risk is now read from the remaining supply and
                      the prescription's validity. */}
                  {supplyLine(review.gapRisk.remainingSupply)}
                  {review.gapRisk.daysToValidUntil !== null
                    ? ` • Receptet gäller ${review.gapRisk.daysToValidUntil} dagar till`
                    : ""}
                </p>
              </div>
            ) : null}

            {/* TES-1030 — her F-07 flags (the v1 risk codes among them), each
                linked to the answer that raised it. Kirill: every flag in the
                overview should lead to its question and answer. */}
            <div>
              <h3 className="text-xs font-semibold text-textSecondary uppercase tracking-wide mb-xs">
                Flaggor
              </h3>
              <RiskFlagList
                flags={
                  (review.transferSummary?.riskFlags ??
                    []) as PreConsultSummary["riskFlags"]
                }
                linkedKey={(f) =>
                  linkedAnswerKey(
                    f.fieldKeys,
                    review.transferSummary?.sections ?? [],
                  )
                }
                onOpen={(key) => openAnswer("f07", key)}
              />
            </div>

            {review.transferSummary ? (
              <details
                open={answersOpen.f07}
                onToggle={(e) => {
                  const open = e.currentTarget.open;
                  setAnswersOpen((o) => ({ ...o, f07: open }));
                }}
              >
                <summary className="cursor-pointer text-sm text-textSecondary">
                  Alla svar i receptövertaget
                </summary>
                <div className="mt-sm">
                  <SummarySections
                    sections={review.transferSummary.sections}
                    anchorScope="f07"
                    highlightKey={
                      highlight?.scope === "f07" ? highlight.key : null
                    }
                  />
                </div>
              </details>
            ) : (
              missingSummary("F-07")
            )}
          </div>
          {rawAnswers(
            "Receptövertag",
            transferSubmission,
            review.templateVersions.transfer,
          )}
        </div>

        {/* F-02 — the health declaration this review was opened on. */}
        <div className="min-w-0">
          {columnHeader(
            "F-02 Hälsodeklaration",
            review.templateVersions.intake,
            intakeSubmission,
          )}
          <div className="flex flex-col gap-md">
            <div>
              <h3 className="text-xs font-semibold text-textSecondary uppercase tracking-wide mb-xs">
                Kontraindikationer och flaggor
              </h3>
              <RiskFlagList
                flags={
                  (review.intakeSummary?.riskFlags ??
                    []) as PreConsultSummary["riskFlags"]
                }
                linkedKey={(f) =>
                  linkedAnswerKey(
                    f.fieldKeys,
                    review.intakeSummary?.sections ?? [],
                  )
                }
                onOpen={(key) => openAnswer("f02", key)}
              />
            </div>
            {review.intakeSummary ? (
              review.intakeSummary.sections.length > 0 ? (
                <details
                  open={answersOpen.f02}
                  onToggle={(e) => {
                    const open = e.currentTarget.open;
                    setAnswersOpen((o) => ({ ...o, f02: open }));
                  }}
                >
                  <summary className="cursor-pointer text-sm text-textSecondary">
                    Alla svar i hälsodeklarationen
                  </summary>
                  <div className="mt-sm">
                    <SummarySections
                      sections={review.intakeSummary.sections}
                      anchorScope="f02"
                      highlightKey={
                        highlight?.scope === "f02" ? highlight.key : null
                      }
                    />
                  </div>
                </details>
              ) : (
                <p className="text-sm text-textSecondary">Inga svar.</p>
              )
            ) : (
              missingSummary("F-02")
            )}
          </div>
          {rawAnswers(
            "Hälsodeklaration",
            intakeSubmission,
            review.templateVersions.intake,
          )}
        </div>
      </div>

      <section className="mb-lg" aria-label="Journalanteckning">
        <h3 className="font-display text-lg mb-sm">Journalanteckning</h3>
        <JournalSectionsCard
          ref={journalRef}
          idPrefix={`transfer-${review.id}`}
          layout={TRANSFER_JOURNAL_LAYOUT}
          load={loadJournal}
          save={saveJournal}
          onUnauthorized={onUnauthorized}
          order={journalOrder}
          sectionAi={journalAi}
          onCompileAnyway={() => void openWebdocStep(true)}
        />
      </section>

      {/* TES-1241 (TES-1240) — the Recept section, after the journal, while
          the case is open. */}
      {isOpen ? (
        <TransferReceptSection
          idPrefix={`transfer-${review.id}-recept`}
          formId={FORM}
          reported={review.reportedTreatment}
          prescriptionDocuments={prescriptionDocuments}
          planned={planned}
          value={recept}
          onChange={onReceptChange}
          disabled={readOnly || busy}
          errors={receptAttempted ? receptErrors : {}}
          returning={returning}
        />
      ) : null}
      {isOpen && noteRoundId ? (
        <div className="mb-lg">
          <CompletionNoteWorkspace
            key={noteRoundId}
            patientId={patientId}
            roundId={noteRoundId}
            onUnauthorized={onUnauthorized}
          />
        </div>
      ) : null}

      <div className="flex flex-col gap-md mt-md">
        {isOpen ? (
          <div className="flex flex-col gap-sm border-t border-surfaceMuted pt-md">
            {review.status === "completion_requested" ? (
              <p className="text-sm text-textSecondary">
                Patienten har inte skickat in kompletteringen än. Du kan besluta
                ändå om det hon skickat i chatten räcker.
              </p>
            ) : null}
            {review.status === "completion_requested" ||
            review.status === "labs_required" ? (
              <CompletionProgress
                patientId={patientId}
                onUnauthorized={onUnauthorized}
              />
            ) : null}
            {/* TES-1243 — the medical decision in the workspace itself, not
                a dialog: the five outcomes in Emelie's order and words, and
                under the chosen one its fields, the notice, "Sammanställ
                journalanteckning" and the Webdoc step. */}
            {readOnly ? null : (
              <fieldset
                className="flex flex-col gap-xs"
                data-testid="transfer-outcomes"
              >
                <legend className="mb-xs font-display text-lg">
                  Medicinskt beslut
                </legend>
                {OUTCOME_ORDER.filter(can).map((o) => (
                  <label key={o} className="flex items-start gap-sm text-sm">
                    <input
                      type="radio"
                      name={`transfer-${review.id}-outcome`}
                      className="mt-[3px] accent-brand"
                      checked={pending === o}
                      disabled={busy}
                      onChange={() => chooseOutcome(o)}
                    />
                    <span>
                      <span className="text-textPrimary">
                        {OUTCOME[o].title}
                      </span>
                      <span className="block text-xs text-textSecondary">
                        {OUTCOME[o].body}
                      </span>
                    </span>
                  </label>
                ))}
              </fieldset>
            )}
          </div>
        ) : null}
      </div>

      {pending && isOpen && !readOnly ? (
        <div className="mt-md">
          <div
            aria-labelledby="transfer-outcome-title"
            className="flex flex-col gap-md rounded-md border border-surfaceMuted p-md"
            data-testid="transfer-decision"
          >
            <h3 id="transfer-outcome-title" className="font-display text-lg">
              {OUTCOME[pending].title}
            </h3>
            {/* TES-950 — the shared completion picker (five options, hints
                about what is on file, blood pressure and pulse pre-ticked when
                missing). It replaced TES-909's seven needs and the required
                reason; the server maps the items onto F-07 sections and CP-06
                tasks. */}
            {pending === "completion_requested" ? (
              <div
                id={fieldDomId(FORM, "completion")}
                tabIndex={-1}
                className="flex flex-col gap-sm"
              >
                <CompletionPicker
                  patientId={patientId}
                  value={completion}
                  onChange={setCompletion}
                  showOutcome={false}
                  disabled={busy}
                  onUnauthorized={onUnauthorized}
                />
                <FieldErrorText message={shownError("completion")} />
                <p className="text-xs text-textSecondary">
                  Patienten ser: ”{COMPLETION_STANDARD_PATIENT_TEXT}”
                </p>
              </div>
            ) : null}
            {pending === "labs_required" ? (
              <fieldset className="flex flex-col gap-xs">
                <legend className="text-xs font-semibold text-textSecondary uppercase tracking-wide mb-xs">
                  Vilka uppgifter ska hon få?
                </legend>
                {/* TES-763 (Emelie's point 5) —
                    "skapar bara de uppgifter läkaren uttryckligen väljer",
                    and "riktad komplettering skapar ingen implicit provbetalning".
                    The list is therefore exhaustive: what is not ticked does
                    not come into being. */}
                {MEDICAL_EVIDENCE_TASK_KEYS.map((task) => (
                  <label
                    key={task}
                    className="flex items-center gap-sm text-sm cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={tasks.includes(task)}
                      onChange={() => toggleTask(task)}
                      disabled={busy}
                    />
                    {REQUEST_TASK_LABEL[task]}
                  </label>
                ))}
                {/* TES-909 — this fieldset is now `labs_required` only, so
                    the text no longer has to explain two outcomes at once.
                    The komplettering's overlap warning is gone with it: a
                    clinical need maps to ONE side, so the double-ask it
                    guarded against can no longer be expressed. */}
                <p className="text-xs text-textSecondary">
                  Inget ikryssat = uppgifterna härleds av ärendetypen
                  (provtagning, betalning, och blodtryck om F-07 inte redan har
                  en mätning).
                </p>
              </fieldset>
            ) : null}
            {/* TES-1242 — the plan chosen in the Recept section is saved
                with the komplettering; no e-prescription is issued. */}
            {asksForEvidence && completionPlan ? receptSummary(true) : null}
            {pending === "approved" ? (
              <div className="flex flex-col gap-sm">
                {/* TES-734 (A-02):
                    "Godkännande kräver verifierad ordination, verified_treatment_phase, current_treatment_started_at och treatment_start_precision."
                    Her own date
                    is shown read-only; the doctor verifies or corrects.
                    TES-1241 — the verified order is the Recept section's,
                    shown here as it will be saved, never typed again. */}
                {receptSummary(false)}
                <div id={fieldDomId(FORM, "clinicalPhase")} tabIndex={-1}>
                  <ClinicalPhaseField
                    idPrefix="transfer-approve"
                    value={phase}
                    onChange={setPhase}
                    disabled={busy}
                  />
                  <FieldErrorText message={shownError("clinicalPhase")} />
                </div>
                <p className="text-xs text-textSecondary">
                  Patientens egen uppgift:{" "}
                  {review.selfReportedTreatmentStartedAt
                    ? `behandlingen började ${review.selfReportedTreatmentStartedAt}`
                    : "vet inte när behandlingen började"}
                  .
                </p>
                <div className="flex gap-sm">
                  <label className="flex flex-col gap-xs text-sm flex-1">
                    <span className="text-textSecondary">
                      Verifierat startdatum
                    </span>
                    <input
                      id={fieldDomId(FORM, "startedAt")}
                      type="date"
                      aria-invalid={shownError("startedAt") ? true : undefined}
                      className={`border rounded-md p-sm text-sm ${
                        shownError("startedAt")
                          ? "border-danger"
                          : "border-surfaceMuted"
                      }`}
                      value={startedAt}
                      onChange={(e) => setStartedAt(e.target.value)}
                      disabled={busy}
                    />
                    <FieldErrorText message={shownError("startedAt")} />
                  </label>
                  <label className="flex flex-col gap-xs text-sm flex-1">
                    <span className="text-textSecondary">Precision</span>
                    <select
                      className="border border-surfaceMuted rounded-md p-sm text-sm"
                      value={precision}
                      onChange={(e) =>
                        setPrecision(e.target.value as TreatmentStartPrecision)
                      }
                      disabled={busy}
                    >
                      {(
                        Object.keys(
                          PRECISION_LABEL,
                        ) as TreatmentStartPrecision[]
                      ).map((p) => (
                        <option key={p} value={p}>
                          {PRECISION_LABEL[p]}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
            ) : null}
            {OUTCOME[pending].needsReason ? (
              <label
                id={fieldDomId(FORM, "reason")}
                className="flex flex-col gap-xs text-sm"
              >
                {OUTCOME[pending].reasonPatientVisible ? (
                  <span className="text-textSecondary">
                    {pending === "cannot_transfer"
                      ? "Medicinsk motivering till patienten (visas i appen)"
                      : "Meddelande till patienten (visas i appen)"}
                  </span>
                ) : null}
                <textarea
                  className="border border-surfaceMuted rounded-md p-sm text-sm min-h-[80px] focus:outline-none focus:border-brand"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={OUTCOME[pending].reasonPlaceholder}
                  disabled={busy}
                />
                <FieldErrorText message={shownError("reason")} />
              </label>
            ) : null}
            {pending === "cannot_transfer" ? (
              <label
                id={fieldDomId(FORM, "carePath")}
                className="flex flex-col gap-xs text-sm"
              >
                <span className="text-textSecondary">
                  Fortsatt vårdväg (visas i appen)
                </span>
                <textarea
                  className="border border-surfaceMuted rounded-md p-sm text-sm min-h-[60px] focus:outline-none focus:border-brand"
                  value={carePath}
                  onChange={(e) => setCarePath(e.target.value)}
                  placeholder="T.ex. fortsätt hos din nuvarande förskrivare, eller kontakta din vårdcentral"
                  disabled={busy}
                />
                <FieldErrorText message={shownError("carePath")} />
              </label>
            ) : null}
            {/* TES-1034 S7 (Emelie, point 2) — before anything is sent, the
                compiled journal note: copied to Webdoc and confirmed, for
                every outcome. */}
            {/* TES-1034 S8 — which option and price the link after the
                approval is sent for: her choice, or the correction. */}
            {pending === "approved" && review.planChoice ? (
              <p
                className="rounded-md bg-surfaceMuted/40 p-sm text-sm"
                data-testid="transfer-sent-plan"
              >
                {sentPlanLine(review.planChoice, planAmounts)}
              </p>
            ) : null}
            <div id={fieldDomId(FORM, "webdoc")} tabIndex={-1}>
              <div
                className="flex flex-col gap-xs rounded-md border border-surfaceMuted p-sm mb-sm"
                data-testid="transfer-webdoc-note"
              >
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void openWebdocStep()}
                  className="self-start rounded-md border border-brand px-md py-sm text-sm font-semibold text-brand hover:bg-brand/5 disabled:opacity-50"
                >
                  {/* TES-1294 — one label, A-02: "Det ska finnas en enda knapp
                      längst ned efter samtliga journalfält: SAMMANSTÄLL
                      JOURNALANTECKNING". */}
                  {summaryProgress ?? "Sammanställ journalanteckning"}
                </button>
                {noteChanged ? (
                  <p className="text-xs text-warning" aria-live="polite">
                    Journalanteckningen har ändrats sedan du bekräftade den.
                    Kopiera den nya texten till Webdoc och bekräfta igen.
                  </p>
                ) : null}
                {webdocNote ? (
                  webdocNote.text ? (
                    <>
                      {/* TES-1312 — beside the note, never in it. */}
                      <AiSummarisedHeadings headings={aiHeadings} />
                      <textarea
                        ref={noteRef}
                        readOnly
                        value={webdocNote.text}
                        rows={Math.min(
                          16,
                          webdocNote.text.split("\n").length + 1,
                        )}
                        onFocus={(e) => e.currentTarget.select()}
                        aria-label="Journalanteckning för Webdoc"
                        className="w-full rounded-md border border-surfaceMuted bg-surface p-sm font-mono text-xs leading-relaxed text-textPrimary"
                      />
                      {webdocNote.skipped.length > 0 ? (
                        <p className="text-xs text-textSecondary">
                          {`Tomma rubriker togs inte med: ${webdocNote.skipped.join(", ")}.`}
                        </p>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => void copyNote()}
                        className="self-start rounded-md bg-brand px-md py-sm text-sm font-semibold text-textOnBrand hover:opacity-90"
                      >
                        Kopiera hela journalanteckningen
                      </button>
                      {copyState !== "idle" ? (
                        <p
                          className="text-xs text-textSecondary"
                          aria-live="polite"
                        >
                          {copyState === "copied"
                            ? "Kopierad — klistra in i Webdoc."
                            : "Kunde inte kopiera automatiskt. Texten är markerad — kopiera den och klistra in i Webdoc."}
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <p className="text-sm text-textSecondary">
                      Inget att kopiera — alla rubriker är tomma. Skriv under
                      rätt rubrik innan du fattar beslutet.
                    </p>
                  )
                ) : null}
              </div>
              {pending === "approved" ? (
                <TreatmentCommentField
                  id="transfer-comment"
                  text={noticeText}
                  value={notice}
                  prefill={noticePrefill}
                  onChange={setNotice}
                  structured={order}
                  // The API refuses an unconfirmed claim (TES-1201); only
                  // the "new_issued" choice confirms a prescription.
                  confirmed={{
                    labOrder: false,
                    prescription: approvalPrescription?.choice === "new_issued",
                  }}
                  disabled={busy}
                />
              ) : null}
              <WebdocControlStep
                idPrefix="transfer"
                journal={
                  webdocNote?.text
                    ? { done: journalDone, onChange: setJournalDone }
                    : undefined
                }
                {...(pending === "approved"
                  ? {
                      // TES-1187 — the prescription status, chosen before
                      // the note is copied: the note journals it.
                      decisionPrescription: true,
                      // TES-1241 — the one choice the Recept section named.
                      decisionChoices,
                      prescription,
                      onPrescriptionChange: setPrescription,
                    }
                  : {})}
                labOrder={needsLabOrder}
                labOrderDone={labOrderDone}
                onLabOrderDoneChange={setLabOrderDone}
                requisitionRef={{
                  value: requisitionRef,
                  onChange: setRequisitionRef,
                }}
                disabled={busy}
              />
              <FieldErrorText message={shownError("webdoc")} />
            </div>
            {error ? <p className="text-danger text-sm">{error}</p> : null}
            <div className="flex gap-sm justify-end">
              <button
                type="button"
                onClick={closeModal}
                className="px-md py-sm rounded-md text-textSecondary hover:bg-surfaceMuted text-sm"
                disabled={busy}
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={() => void confirm()}
                disabled={busy}
                className={`px-md py-sm rounded-md text-sm hover:opacity-90 disabled:opacity-60 ${
                  pending === "approved"
                    ? "bg-brand text-textOnBrand"
                    : pending === "cannot_transfer"
                      ? "bg-danger text-white"
                      : "bg-textPrimary text-surface"
                }`}
              >
                {busy
                  ? "Sparar…"
                  : pending === "approved"
                    ? PUBLISH_DECISION_AND_SEND_LINK_CTA
                    : needsLabOrder
                      ? PUBLISH_TO_PATIENT_CTA
                      : OUTCOME[pending].cta}
              </button>
            </div>
            {summaryLine && !busy ? (
              <p role="alert" className="-mt-sm text-right text-xs text-danger">
                {summaryLine}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
